import React, { useState } from 'react';
import { 
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { DonateButtonVariant } from '../Button/Button.jsx';


// Import assets
import { Images } from '../../../utils/Images';
import { getImageSrc } from '../../../utils/imageMapping';
import { getCampaignShareDetails } from '../../../api/services/campaignService';
import { getCampaignUrl } from '../../../utils/slugUtils';
import { getAvailableSocialPlatforms, buildSocialShareUrl } from '../../../utils/socialShare';
import { formatCompactInr } from "../../../utils/formatCompactInr";

// Styled components
const StyledCard = styled(Card)(({ theme, isMobile, isCampaign }) => ({
  width: isMobile ? '100%' : isCampaign ? '417px' : '417px',
  minWidth: isMobile ? '290px' : 'auto',
  // height: 'auto',
  background: '#FFFFFF',
  boxShadow: '0px 10px 26px rgba(0, 0, 0, 0.04)',
  borderRadius: isMobile ? '12px' : '16px',
  overflow: 'visible',
  fontFamily: "'Red Hat Display', sans-serif",
  maxWidth: isMobile ? '100%' : isCampaign ? '100%' : '100%',
  cursor: 'pointer',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0px 12px 30px rgba(0, 0, 0, 0.08)',
  },
}));

const ImageSection = styled(Box)(({ theme, isMobile, customMobileHeight }) => ({
  width: '100%',
  height: isMobile ? (customMobileHeight || '156px') : '217px',
  position: 'relative',
  borderRadius: isMobile ? '12px' : '20px',
  overflow: 'hidden',
  marginBottom: isMobile ? '14px' : '18px',
}));

const CampaignImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: 'inherit',
});

const TaxBenefitChip = styled(Box)(({ isMobile }) => ({
  position: 'absolute',
  right: 0,
  top: 0,
  background: 'linear-gradient(295deg, #EC1B26, #FF4A60)',
  color: '#FFFFFF',
  fontFamily: 'Red Hat Display, sans-serif',
  fontWeight: 700,
  fontSize: isMobile ? '10px' : '12px',
  lineHeight: '16px',
  letterSpacing: '-0.24px',
  height: isMobile ? '28px' : '36px',
  padding: isMobile ? '6px 10px' : '6px 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderTopLeftRadius: '3px',
  borderTopRightRadius: '20px',
  borderBottomLeftRadius: '20px',
  borderBottomRightRadius: '3px',
  whiteSpace: 'nowrap',
}));

const ProgressContainer = styled(Box)(({ theme, isMobile }) => ({
  position: 'relative',
  marginBottom: isMobile ? '40px' : '50px',
}));

const ProgressLabel = styled(Box)(({ theme, isMobile }) => ({
  position: 'absolute',
  top: '0px',
  background: '#EC1B26',
  color: '#FFFFFF',
  fontFamily: 'Red Hat Display, sans-serif',
  fontWeight: 700,
  fontSize: isMobile ? '12px' : '13px',
  lineHeight: '1',
  height: isMobile ? '20px' : '22px',
  borderRadius: '4px',
  transform: 'translateX(50%)',
  padding: isMobile ? '0 6px' : '0 8px',
  display: 'flex',
  alignItems: 'center',
}));

const StatsContainer = styled(Box)(({ theme, isMobile }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: isMobile ? '16px' : '20px',
  gap: isMobile ? '6px' : '8px',
  marginTop: isMobile ? '8px' : '10px',
}));

const StatItem = styled(Box)(({ theme, isMobile }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: isMobile ? '8px' : '10px',
  height: isMobile ? '32px' : '38px',
  flex: 1,
  minWidth: 0,
}));

const RupeeContainer = styled(Box)(({ theme, isMobile, badgeIcon }) => ({
  width: isMobile ? '28px' : '30px',
  height: isMobile ? '28px' : '30px',
  background: badgeIcon ? 'transparent' : 'rgba(236, 27, 38, 0.1)',
  border: badgeIcon ? 'none' : '0.25px solid rgba(242, 178, 186, 0.1)',
  borderRadius: badgeIcon ? '0' : '7.4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& img': {
    width: badgeIcon ? (isMobile ? '28px' : '30px') : (isMobile ? '14px' : '20px'),
    height: badgeIcon ? (isMobile ? '28px' : '30px') : (isMobile ? '14px' : '20px'),
    objectFit: 'contain',
  },
}));

const StatValue = styled(Typography)(({ theme, isMobile }) => ({
  fontFamily: 'Red Hat Display',
  fontWeight: '800',
  fontSize: isMobile ? '12px' : '13px',
  lineHeight: isMobile ? '16px' : '17px',
  color: '#1D1D1B',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '100%',
}));

const StatLabel = styled(Typography)(({ theme, isMobile }) => ({
  fontFamily: 'Red Hat Display',
  fontWeight: '500',
  fontSize: isMobile ? '10px' : '11px',
  lineHeight: isMobile ? '13px' : '14px',
  color: '#686866',
  textTransform: 'capitalize',
}));

const SocialIconButton = styled(IconButton)(({ theme, isMobile }) => ({
  width: isMobile ? '28px' : '40px', // Reduced from 36px to 28px
  height: isMobile ? '28px' : '40px', // Reduced from 36px to 28px
  border: '1px solid #E8E8E8',
  borderRadius: '10px',
  padding: isMobile ? '0px' : '0px', // Reduced from 6px to 4px
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f8f8f8',
    borderColor: '#d0d0d0',
  },
  '& img': {
    width: isMobile ? '16px' : '24px', // Reduced from 20px to 16px
    height: isMobile ? '16px' : '24px', // Reduced from 20px to 16px
  },
}));

// Helper function to format currency
const formatCurrency = (amount) => {
  return formatCompactInr(amount);
};

// Helper function to calculate progress percentage
const calculateProgress = (raised, goal) => {
  return Math.min(Math.round((raised / goal) * 100), 100);
};

const CampaignCard = ({ campaignData, isCampaign = false, customMobileImageHeight }) => {
  const {
    id,
    image,
    taxBenefit,
    title,
    goalAmount,
    raisedAmount,
    backers,
    category,
    location,
    socialMediaLinks,
    hide_goal = 0,
    hide_raised = 0,
    campaign_type = false
  } = campaignData;

  // Normalize hide_goal and hide_raised to numbers (handle string "1" or "0")
  const hideGoal = Number(hide_goal) || 0;
  const hideRaised = Number(hide_raised) || 0;

  const progressPercentage = calculateProgress(raisedAmount, goalAmount);
  const remainingAmount = goalAmount - raisedAmount;
  const navigate = useNavigate();
  const [shareLoading, setShareLoading] = useState({});

  // Social icons are always shown (Figma design); use API flags when available
  const availableSocialPlatforms = getAvailableSocialPlatforms(socialMediaLinks);
  const hasAnySocialPlatform = Object.values(availableSocialPlatforms).some(Boolean);

  // Handle donate button click
  const handleCardClick = () => {
    // Navigate to campaign detail page with campaign slug
    const url = getCampaignUrl(campaignData);
    if (url) {
      navigate(url);
    } else {
      // Fallback to campaigns list if no redirects
      navigate('/campaigns');
    }
  };

  const handleDonateClick = (e) => {
    e.stopPropagation(); // Prevent card click from firing
    console.log('Donate button clicked for campaign:', title);
    // Navigate to campaign detail page with campaign slug
    const url = getCampaignUrl(campaignData);
    if (url) {
      navigate(url);
    } else {
      // Fallback to campaigns list if no redirects
      navigate('/campaigns');
    }
  };

  const handleSocialShare = async (platform, e) => {
    e.stopPropagation(); // Prevent card click from firing
    
    // Set loading state for this platform
    setShareLoading(prev => ({ ...prev, [platform]: true }));
    
    try {
      // Fetch share details from refer a friend API
      const shareData = await getCampaignShareDetails(id);

      if (shareData) {
        // Map platform names to share_links keys
        const platformKeyMap = {
          'Facebook': 'facebook',
          'LinkedIn': 'linkedin',
          'Twitter': 'twitter',
        };

        const platformKey = platformKeyMap[platform];
        const campaignUrl = shareData.share_links?.copy || shareData.link || '';
        const shareUrl =
          shareData.share_links?.[platformKey] ||
          buildSocialShareUrl(platformKey, campaignUrl, shareData.message);

        if (shareUrl) {
          window.open(shareUrl, '_blank', 'noopener,noreferrer');
        } else {
          console.warn(`Share URL not available for ${platform}`);
        }
      } else {
        console.warn('Share data not available');
      }
    } catch (error) {
      console.error(`Failed to fetch share details for ${platform}:`, error);
    } finally {
      setShareLoading(prev => ({ ...prev, [platform]: false }));
    }
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box>
      <StyledCard 
        isMobile={isMobile} 
        isCampaign={isCampaign}
        onClick={handleCardClick}
      >
        <CardContent sx={{ 
          padding: isMobile ? '12px' : '16px', 
          '&:last-child': { paddingBottom: isMobile ? '16px' : '20px' } 
        }}>
          {/* Image Section */}
          <ImageSection isMobile={isMobile} customMobileHeight={customMobileImageHeight}>
            <CampaignImage src={getImageSrc(image) || Images.fallBackImageCard} alt={title} />
            {Boolean(taxBenefit) && <TaxBenefitChip isMobile={isMobile}>{taxBenefit}</TaxBenefitChip>}
          </ImageSection>

          {/* Main Content */}
          <Box sx={{ width: '100%' }}>
            {/* Title */}
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Red Hat Display',
                fontWeight: '700',
                fontSize: isMobile ? '14px' : '18px',
                lineHeight: isMobile ? '18px' : '26px',
                color: '#1D1D1B',
                textTransform: 'capitalize',
                marginBottom: isMobile ? '8px' : '10px',
                minHeight: isMobile ? '36px' : '52px',
                maxHeight: isMobile ? '36px' : '52px',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
              }}
            >
              {title}
            </Typography>

            {/* Location (optional, e.g. temple campaigns) */}
            {location && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: isMobile ? '10px' : '14px' }}>
                <Box component="img" src={Images.locationPin} alt="" sx={{ width: isMobile ? 16 : 20, height: isMobile ? 16 : 20 }} />
                <Typography
                  sx={{
                    fontFamily: 'Red Hat Display',
                    fontWeight: 500,
                    fontSize: isMobile ? '12px' : '14px',
                    lineHeight: 1,
                    color: '#686866',
                    textTransform: 'capitalize',
                  }}
                >
                  {location}
                </Typography>
              </Box>
            )}

            {/* Progress Bar */}
            <ProgressContainer isMobile={isMobile}>
              <Box
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: isMobile ? '10px' : '12px',
                  left: '0px',
                  top: isMobile ? '3px' : '4px',
                  background: '#E8E8E8',
                  opacity: 0.6,
                  borderRadius: '20px',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  width: `${progressPercentage}%`,
                  height: '6px',
                  left: '3px',
                  top: isMobile ? '6px' : '7px',
                  background: 'linear-gradient(90deg, #FF4A60 0%, #EC1B26 100%)',
                  borderRadius: '4px',
                }}
              />
              <ProgressLabel
                isMobile={isMobile}
                sx={{
                  left: progressPercentage === 0
                    ? isMobile ? '0px' : '-20px'
                    : progressPercentage <= 10
                    ? isMobile ? '-8px' : '-10px'
                    : progressPercentage >= 90
                    ? isMobile ? 'calc(100% - 60px)' : 'calc(100% - 72px)'
                    : `calc(${progressPercentage}% - ${isMobile ? '18px' : '22px'})`,
                }}
              >
                {`${progressPercentage}%`}
              </ProgressLabel>
            </ProgressContainer>

            {/* Statistics */}
            <StatsContainer isMobile={isMobile}>
              {/* Case 1: hide_goal = 0, hide_raised = 0: Show Goal, Raised, Donors */}
              {hideGoal === 0 && hideRaised === 0 && (
                <>
                  <StatItem isMobile={isMobile}>
                    <RupeeContainer isMobile={isMobile}>
                      <img src={Images.cardGoal} alt="Goal" />
                    </RupeeContainer>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      <StatLabel isMobile={isMobile}>Goal</StatLabel>
                      <StatValue isMobile={isMobile}>{formatCurrency(goalAmount)}</StatValue>
                    </Box>
                  </StatItem>
                  <Divider orientation="vertical" flexItem sx={{ height: isMobile ? '32px' : '38px' }} />

                  <StatItem isMobile={isMobile}>
                    <RupeeContainer isMobile={isMobile} badgeIcon>
                      <img src={Images.cardRaised} alt="Raised" />
                    </RupeeContainer>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      <StatLabel isMobile={isMobile}>Raised</StatLabel>
                      <StatValue isMobile={isMobile}>{formatCurrency(raisedAmount)}</StatValue>
                    </Box>
                  </StatItem>
                  <Divider orientation="vertical" flexItem sx={{ height: isMobile ? '32px' : '38px' }} />
                </>
              )}

              {/* Case 2: hide_goal = 1, hide_raised = 0: Show Raised (in Goal's place), Text, Donors */}
              {hideGoal === 1 && hideRaised === 0 && (
                <>
                  <StatItem isMobile={isMobile}>
                    <RupeeContainer isMobile={isMobile} badgeIcon>
                      <img src={Images.cardRaised} alt="Raised" />
                    </RupeeContainer>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      <StatLabel isMobile={isMobile}>Raised</StatLabel>
                      <StatValue isMobile={isMobile}>{formatCurrency(raisedAmount)}</StatValue>
                    </Box>
                  </StatItem>
                  <Divider orientation="vertical" flexItem sx={{ height: isMobile ? '32px' : '38px' }} />

                  <StatItem isMobile={isMobile} sx={{ justifyContent: 'center', flex: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center', textAlign: 'center' }}>
                      <StatLabel isMobile={isMobile} sx={{ 
                        fontSize: isMobile ? '10px' : '12px',
                        lineHeight: isMobile ? '14px' : '16px',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word'
                      }}>
                        For the Profiled patient and other patient in need
                      </StatLabel>
                    </Box>
                  </StatItem>
                  <Divider orientation="vertical" flexItem sx={{ height: isMobile ? '32px' : '38px' }} />
                </>
              )}

              {/* Case 3: hide_goal = 1, hide_raised = 1: Show Text, Donors */}
              {hideGoal === 1 && hideRaised === 1 && (
                <>
                  <StatItem isMobile={isMobile} sx={{ justifyContent: 'flex-start', flex: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-start', textAlign: 'left' }}>
                      <StatLabel isMobile={isMobile} sx={{ 
                        fontSize: isMobile ? '10px' : '12px',
                        lineHeight: isMobile ? '14px' : '16px',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word'
                      }}>
                        For the Profiled patient and other patient in need
                      </StatLabel>
                    </Box>
                  </StatItem>
                  <Divider orientation="vertical" flexItem sx={{ height: isMobile ? '32px' : '38px' }} />
                </>
              )}

              {/* Donors - always shown */}
              <StatItem isMobile={isMobile}>
                <RupeeContainer isMobile={isMobile}>
                  <img src={Images.cardBackers} alt="Donors" />
                </RupeeContainer>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  <StatLabel isMobile={isMobile}>Donors</StatLabel>
                  <StatValue isMobile={isMobile}>{backers.toLocaleString()}</StatValue>
                </Box>
              </StatItem>
            </StatsContainer>

            {/* Main Divider */}
            <Divider sx={{ marginBottom: isMobile ? '16px' : '20px' }} />

            {/* Footer */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: hasAnySocialPlatform ? 'space-between' : 'flex-end',
                alignItems: 'center',
                width: '100%',
                height: isMobile ? '32px' : '44px', // Reduced from 40px to 32px for mobile
              }}
            >
              {/* Social Media Icons - Only show if status === 1 and platform is enabled (true) */}
              {hasAnySocialPlatform && (
                <Box 
                  sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {availableSocialPlatforms.facebook && (
                    <SocialIconButton
                      isMobile={isMobile}
                      onClick={(e) => handleSocialShare('Facebook', e)}
                      aria-label="Share on Facebook"
                      disabled={shareLoading.Facebook}
                    >
                      <img src={Images.facebook} alt="Facebook" />
                    </SocialIconButton>
                  )}
                  {availableSocialPlatforms.linkedin && (
                    <SocialIconButton
                      isMobile={isMobile}
                      onClick={(e) => handleSocialShare('LinkedIn', e)}
                      aria-label="Share on LinkedIn"
                      disabled={shareLoading.LinkedIn}
                    >
                      <img src={Images.linkedin} alt="LinkedIn" />
                    </SocialIconButton>
                  )}
                  {availableSocialPlatforms.twitter && (
                    <SocialIconButton
                      isMobile={isMobile}
                      onClick={(e) => handleSocialShare('Twitter', e)}
                      aria-label="Share on Twitter"
                      disabled={shareLoading.Twitter}
                    >
                      <img src={Images.twitter} alt="Twitter" />
                    </SocialIconButton>
                  )}
                </Box>
              )}

              {/* Donate Button */}
              <DonateButtonVariant
                text="Donate Now"
                onClick={handleDonateClick}
                isMobile={isMobile}
              />
            </Box>
          </Box>
        </CardContent>
      </StyledCard>
    </Box>
  );
};

export default CampaignCard;